/**
 * What the ground is, for the hazards: the water and ice of a barrier (mission 6's frozen
 * river), its fords, its cracked patches - and what all of that, the minefields and the
 * mini-bunkers do to the soldiers' navigation grid.
 *
 * A leaf on purpose: `statics.ts` and `enemies/perception.ts` import this file, so it
 * imports nothing but tuning and types.
 */

import { cos, sin } from '../math';
import { HAZARDS, SPECIALS } from '../tuning';
import type { MissionDef, PropState, WaterBarrierDef, World } from '../types';

/** Own copy of the point-to-polyline distance: `statics.ts` imports this file, so it cannot be borrowed back. */
function courseDistance(points: ReadonlyArray<readonly [number, number]>, x: number, z: number): number {
  let best = Infinity;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    if (!a || !b) continue;
    const vx = b[0] - a[0];
    const vz = b[1] - a[1];
    const len2 = vx * vx + vz * vz;
    const t = len2 === 0 ? 0 : Math.min(1, Math.max(0, ((x - a[0]) * vx + (z - a[1]) * vz) / len2));
    const dx = x - (a[0] + vx * t);
    const dz = z - (a[1] + vz * t);
    best = Math.min(best, dx * dx + dz * dz);
  }
  return Math.sqrt(best);
}

/** The river (frozen or not) this point lies in, or null. */
export function barrierAt(def: MissionDef, x: number, z: number): WaterBarrierDef | null {
  for (const b of def.waterBarriers) {
    if (courseDistance(b.course.points, x, z) <= b.course.width / 2) return b;
  }
  return null;
}

/**
 * A crossing whose deck has come down (ROUND4 §1): the `bridge` prop that stood on it is destroyed, so the
 * lane is shut - for the garrison's navigation and, through the span's rubble, for the ranger's feet too.
 * Read off the world's props, which is why `buildStatics` passes them in and nothing here caches it.
 */
export function closedCrossings(def: MissionDef, props: readonly PropState[]): number[] {
  const out: number[] = [];
  for (const p of def.props) {
    if (p.kind !== 'bridge') continue;
    if (props.find((s) => s.id === p.id)?.destroyed === true) out.push(p.x);
  }
  return out;
}

/** On a ford, or on ice thick enough to trust: crossings are x-positions along the course. */
export function atCrossing(barrier: WaterBarrierDef, x: number, closed: readonly number[] = []): boolean {
  for (const cx of barrier.crossings) {
    if (Math.abs(x - cx) > HAZARDS.crossingHalfWidth) continue;
    if (closed.some((c) => Math.abs(c - cx) < 0.5)) continue;
    return true;
  }
  return false;
}

/**
 * Standing on a bridge deck that is still up. The deck is a run of `length` along the prop's yaw,
 * as wide as the render draws it (`BRIDGE_HALF_WIDTH` is `crossingHalfWidth + 0.2`, so the figure
 * lives here and the two cannot drift).
 *
 * Found by the WADE stream, round five: `barrierWaterAt` asked only whether the barrier's course
 * ran under his feet, so a man walking a 14 m bridge was charged `wadeSpeed`, `wadeFatiguePerSecond`
 * and `wadeNoise` the whole way across - slowed, tired and noisy, on a dry deck. The renderer drew
 * him correctly above the water, so the picture and the rule disagreed in plain sight.
 */
export function onBridgeDeck(def: MissionDef, x: number, z: number): boolean {
  const halfWidth = HAZARDS.crossingHalfWidth + 0.2;
  for (const p of def.props) {
    if (p.kind !== 'bridge') continue;
    const dx = x - p.x;
    const dz = z - p.z;
    const c = cos(p.yaw);
    const sn = sin(p.yaw);
    // Into the deck's own frame: along its length, and across its width.
    const along = dx * c - dz * sn;
    const across = dx * sn + dz * c;
    if (Math.abs(along) <= (p.length ?? 0) / 2 && Math.abs(across) <= halfWidth) return true;
  }
  return false;
}

/**
 * Open water of a barrier underfoot. `statics.waterAt` adds the ponds to this. A man on a standing
 * bridge is over the water, not in it - see `onBridgeDeck`. A deck that has been blown drops him in,
 * which is what the span's rubble is for.
 */
export function barrierWaterAt(def: MissionDef, x: number, z: number): boolean {
  if (barrierAt(def, x, z)?.surface !== 'water') return false;
  return !onBridgeDeck(def, x, z);
}

export function iceAt(def: MissionDef, x: number, z: number): boolean {
  return barrierAt(def, x, z)?.surface === 'ice';
}

/** Cracked ice that gives way (02 §4). A marked crossing is sound whatever is drawn over it - until its deck falls. */
export function weakIceAt(def: MissionDef, x: number, z: number, closed: readonly number[] = []): boolean {
  const b = barrierAt(def, x, z);
  if (!b || b.surface !== 'ice' || atCrossing(b, x, closed)) return false;
  for (const c of b.weakIce) if (within(x, z, c.x, c.z, c.r)) return true;
  return false;
}

/** On the ground and still a body in the world: what a hazard or a machine can act on. */
export function rangerOnFoot(world: World): boolean {
  const s = world.ranger.status;
  return world.insertion.phase === 'ground' && (s === 'active' || s === 'downed' || s === 'boarding');
}

/** Is (ax, az) within `d` metres of (bx, bz)? */
export function within(ax: number, az: number, bx: number, bz: number, d: number): boolean {
  return (ax - bx) * (ax - bx) + (az - bz) * (az - bz) <= d * d;
}

/** Crawling under water: hidden from every soldier (02 §6.1). Kept current by `stepHazards`. */
export function rangerSubmerged(world: World): boolean {
  return world.hazards.submergedTicks > 0;
}

/** Held in a searchlight beam: every soldier sees him as if by day (02 §9.2). Kept current by `stepHazards`. */
export function rangerLit(world: World): boolean {
  return world.hazards.litTicks > 0;
}

/**
 * The hazards' share of the navigation grid, called by `buildStatics`:
 *  - open water blocks soldiers except at a ford; ice is walkable, cracked or not (that a
 *    hunting man will run straight over cracked ice is the lure - see `water.ts`);
 *  - soldiers never route through their own minefields: a heavy cost, not a block, so a man
 *    whose goal lies inside one can still get there. The minitank ignores costs altogether;
 *  - they give a mini-bunker's motion detector a wide berth. Cost only, never a block: every spawn must
 *    stay on a free cell (the generator validates that), and the bunker's own cell is a spawn.
 */
export function applyHazardNav(
  def: MissionDef,
  width: number,
  height: number,
  blocked: Uint8Array,
  cost: Float32Array,
  closed: readonly number[] = [],
): void {
  const raise = (i: number, c: number): void => {
    if ((cost[i] ?? 1) < c) cost[i] = c;
  };
  if (def.waterBarriers.some((b) => b.surface === 'water')) {
    for (let z = 0; z < height; z++) {
      for (let x = 0; x < width; x++) {
        const b = barrierAt(def, x + 0.5, z + 0.5);
        if (b && b.surface === 'water' && !atCrossing(b, x + 0.5, closed)) blocked[z * width + x] = 1;
      }
    }
  }
  type Visit = (i: number, px: number, pz: number) => void;
  const cells = (x0: number, z0: number, x1: number, z1: number, visit: Visit): void => {
    const xEnd = Math.min(width - 1, Math.floor(x1));
    for (let z = Math.max(0, Math.floor(z0)); z <= Math.min(height - 1, Math.floor(z1)); z++) {
      for (let x = Math.max(0, Math.floor(x0)); x <= xEnd; x++) visit(z * width + x, x + 0.5, z + 0.5);
    }
  };
  for (const f of def.mineFields) {
    cells(f.rect.x0 - 0.5, f.rect.z0 - 0.5, f.rect.x1 + 0.5, f.rect.z1 + 0.5, (i) => raise(i, HAZARDS.mineNavCost));
  }
  for (const s of def.spawns) {
    if (s.kind !== 'miniBunker') continue;
    const reach = SPECIALS.bunkerRadius;
    cells(s.x - reach, s.z - reach, s.x + reach, s.z + reach, (i, px, pz) => {
      if (within(px, pz, s.x, s.z, reach)) raise(i, SPECIALS.bunkerNavCost);
    });
  }
}
